import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Target,
  Shield,
  Cpu,
  Users,
  CheckCircle,
  XCircle,
  ArrowRight,
  Award,
  Zap,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';

const advantages = [
  {
    icon: Target,
    title: 'Unmatched Accuracy',
    description: 'Our locators are trained to exceed industry standards, supported by advanced locating tools and AI-driven verification.',
    highlight: 'AI-verified accuracy'
  },
  {
    icon: Shield,
    title: 'Safety as a Culture',
    description: 'Every project begins with a hazard assessment and ends with documented safety compliance. Zero-incident mindset.',
    highlight: 'Zero-incident record'
  },
  {
    icon: Cpu,
    title: 'Technology-Enabled Operations',
    description: 'Digital workflows, automated reporting, and real-time visibility ensure transparency and accountability.',
    highlight: 'Real-time reporting'
  },
  {
    icon: Users,
    title: 'Scalable Workforce',
    description: '130+ experienced locators across GA, TN, and IL—ready to support utilities, contractors, and municipalities.',
    highlight: '130+ expert locators'
  }
];

const comparison = [
  {
    feature: 'AI-Powered Quality Verification',
    usg: true,
    others: false
  },
  {
    feature: 'Real-Time Digital Documentation',
    usg: true,
    others: false
  },
  {
    feature: 'Photo-Verified Markouts',
    usg: true,
    others: false
  },
  {
    feature: 'GPS-Enabled Mapping',
    usg: true,
    others: true
  },
  {
    feature: '24/7 Emergency Response',
    usg: true,
    others: false
  },
  {
    feature: 'Electromagnetic & GPR Technology',
    usg: true,
    others: true
  },
  {
    feature: 'Root-Cause Analysis & Corrective Action',
    usg: true,
    others: false
  },
  {
    feature: 'Proactive Risk Identification',
    usg: true,
    others: false
  },
  {
    feature: 'Hazard Assessment on Every Project',
    usg: true,
    others: false
  },
  {
    feature: 'Skytec Operational Backbone',
    usg: true,
    others: false
  }
];

const stats = [
  { value: '99%', label: 'Locate Accuracy' },
  { value: '24/7', label: 'Emergency Availability' },
  { value: '130+', label: 'Expert Locators' },
  { value: '0', label: 'Safety Incidents' }
];

export default function ComparePage() {
  return (
    <div className="bg-skytec-bg pt-20">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden" data-testid="compare-hero">
        <div className="absolute inset-0 scan-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">The USG Difference</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-skytec-fg mt-4 mb-6 uppercase tracking-tight">
              WHY CLIENTS <span className="neon-text">CHOOSE USG</span>
            </h1>
            <p className="text-skytec-fg/60 max-w-3xl mx-auto text-lg">
              Next-generation utility locating backed by Skytec's operational discipline and technology leadership. 
              See how USG delivers unmatched reliability for critical infrastructure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Advantages */}
      <section className="py-24 bg-skytec-surface border-y border-skytec-border" data-testid="advantages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Our Advantages</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              KEY DIFFERENTIATORS
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {advantages.map((advantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-skytec-bg rounded-2xl border border-skytec-border p-8 relative overflow-hidden group hover:border-skytec-primary/30 transition-all"
              >
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-skytec-primary-dim text-skytec-primary text-xs font-mono rounded-full">
                    {advantage.highlight}
                  </span>
                </div>
                <div className="w-14 h-14 bg-skytec-primary-dim rounded-xl flex items-center justify-center mb-6">
                  <advantage.icon className="w-7 h-7 text-skytec-primary" />
                </div>
                <h3 className="font-heading text-xl font-bold text-skytec-fg mb-3 uppercase">
                  {advantage.title}
                </h3>
                <p className="text-skytec-fg/60">
                  {advantage.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24" data-testid="comparison-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Feature Comparison</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              USG VS. THE COMPETITION
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-skytec-surface rounded-2xl border border-skytec-border overflow-hidden"
          >
            {/* Header */}
            <div className="grid grid-cols-3 gap-4 p-6 bg-skytec-bg border-b border-skytec-border">
              <div className="font-heading font-bold text-skytec-fg">Feature</div>
              <div className="text-center font-heading font-bold text-skytec-primary">USG</div>
              <div className="text-center font-heading font-bold text-skytec-fg/60">Others</div>
            </div>
            
            {/* Rows */}
            {comparison.map((item, index) => (
              <div 
                key={index}
                className={`grid grid-cols-3 gap-4 p-6 ${index % 2 === 0 ? 'bg-skytec-surface' : 'bg-skytec-bg'} ${index !== comparison.length - 1 ? 'border-b border-skytec-border' : ''}`}
              >
                <div className="text-skytec-fg/80 text-sm">{item.feature}</div>
                <div className="flex justify-center">
                  {item.usg ? (
                    <CheckCircle className="w-6 h-6 text-skytec-accent" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-500" />
                  )}
                </div>
                <div className="flex justify-center">
                  {item.others ? (
                    <CheckCircle className="w-6 h-6 text-skytec-fg/40" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-500/60" />
                  )}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-skytec-surface border-y border-skytec-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="font-heading text-4xl font-bold text-skytec-primary mb-2">{stat.value}</div>
                <div className="text-skytec-fg/60">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Skytec Backbone */}
      <section className="py-24" data-testid="backbone-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-skytec-primary/10 to-skytec-secondary/10 rounded-2xl border border-skytec-border p-8 lg:p-12"
          >
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <Award className="w-12 h-12 text-skytec-primary mb-4" />
                <h3 className="font-heading text-2xl font-bold text-skytec-fg mb-4 uppercase">
                  Skytec Operational Backbone
                </h3>
                <p className="text-skytec-fg/70 mb-6">
                  USG benefits from Skytec's proven leadership in field operations, compliance, and technology integration. 
                  This partnership brings operational discipline and technology leadership to every project we undertake.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-skytec-fg/80">
                    <CheckCircle className="w-5 h-5 text-skytec-accent mr-3" />
                    Proven field operations methodology
                  </li>
                  <li className="flex items-center text-skytec-fg/80">
                    <CheckCircle className="w-5 h-5 text-skytec-accent mr-3" />
                    Industry-leading compliance standards
                  </li>
                  <li className="flex items-center text-skytec-fg/80">
                    <CheckCircle className="w-5 h-5 text-skytec-accent mr-3" />
                    Cutting-edge technology integration
                  </li>
                </ul>
              </div>
              <div className="flex justify-center">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-6 bg-skytec-bg rounded-xl border border-skytec-border text-center">
                    <Zap className="w-8 h-8 text-skytec-primary mx-auto mb-2" />
                    <div className="font-heading font-bold text-skytec-fg">Fast Response</div>
                  </div>
                  <div className="p-6 bg-skytec-bg rounded-xl border border-skytec-border text-center">
                    <Shield className="w-8 h-8 text-skytec-secondary mx-auto mb-2" />
                    <div className="font-heading font-bold text-skytec-fg">Safety First</div>
                  </div>
                  <div className="p-6 bg-skytec-bg rounded-xl border border-skytec-border text-center">
                    <Target className="w-8 h-8 text-skytec-accent mx-auto mb-2" />
                    <div className="font-heading font-bold text-skytec-fg">Precision</div>
                  </div>
                  <div className="p-6 bg-skytec-bg rounded-xl border border-skytec-border text-center">
                    <Clock className="w-8 h-8 text-skytec-primary mx-auto mb-2" />
                    <div className="font-heading font-bold text-skytec-fg">24/7 Ready</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-skytec-surface border-t border-skytec-border" data-testid="compare-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mb-6 uppercase">
              READY TO EXPERIENCE THE DIFFERENCE?
            </h2>
            <p className="text-skytec-fg/60 max-w-2xl mx-auto mb-10">
              Join the utilities, contractors, and municipalities who trust USG for 
              precision locating and damage prevention.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-bold"
                  data-testid="compare-contact-btn"
                >
                  GET A QUOTE
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-skytec-fg/30 text-skytec-fg hover:bg-skytec-fg/10 font-heading"
                >
                  VIEW OUR SERVICES
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
